import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import './ChartOfAccounts.css';
import "react-datepicker/dist/react-datepicker.css";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextField from "@mui/material/TextField";
import dayjs from "dayjs";

const ChartOfAccounts = () => {
  // State variables
  const [mainAccounts, setMainAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [openingDifference, setOpeningDifference] = useState({ balance: 0, dc: '' });
  const [stats, setStats] = useState({
    totalGroups: 0,
    totalLedgers: 0,
    totalBalance: 0,
  });

  // Date filter state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showDateFilters, setShowDateFilters] = useState(false);
  const getDayjsDate = (dateString) => {
    return dateString ? dayjs(dateString) : null;
  };
  // API configuration
  const API_URL = process.env.REACT_APP_WEBZASH_URL || 'http://localhost/webzash-integration';
  const BACKEND_URL = `${API_URL}/api/accounts/index`;

  const headers = {
    'x-api-key': process.env.REACT_APP_API_KEY || 'demo-key',
    'Accept': 'application/json',
  };

  // NEW: Build group hierarchy for recursive calculation
  const buildGroupHierarchy = useCallback((accountsList) => {
    const groups = accountsList.filter(acc => acc.type === 'group');
    const hierarchy = {};

    // Initialize hierarchy
    groups.forEach(group => {
      hierarchy[group.id] = {
        ...group,
        children: [],
        childGroups: [],
        childLedgers: []
      };
    });

    // Build parent-child relationships
    groups.forEach(group => {
      if (group.parent_id && hierarchy[group.parent_id]) {
        hierarchy[group.parent_id].childGroups.push(group.id);
      }
    });

    // Add ledgers to their parent groups
    accountsList.forEach(account => {
      if (account.type === 'ledger' && hierarchy[account.group_id]) {
        hierarchy[account.group_id].childLedgers.push(account);
      }
    });

    return hierarchy;
  }, []);

  // NEW: Recursively calculate group totals including child groups
  const calculateGroupTotals = useCallback((accountsList, hierarchy) => {
    const groupTotals = {};
    const groupOpeningTotals = {};

    // Initialize all groups with zero totals
    Object.keys(hierarchy).forEach(groupId => {
      groupTotals[groupId] = { total: 0, drTotal: 0, crTotal: 0 };
      groupOpeningTotals[groupId] = { total: 0, drTotal: 0, crTotal: 0 };
    });

    // Function to recursively calculate totals for a group
    const calculateGroupRecursive = (groupId) => {
      const group = hierarchy[groupId];
      let drTotal = 0;
      let crTotal = 0;
      let openingDrTotal = 0;
      let openingCrTotal = 0;

      // Add direct ledger balances
      if (group.childLedgers && group.childLedgers.length > 0) {
        group.childLedgers.forEach(ledger => {
          // Current balance
          if (ledger.current_balance_dc === 'Dr') {
            drTotal += Math.abs(ledger.current_balance || 0);
          } else if (ledger.current_balance_dc === 'Cr') {
            crTotal += Math.abs(ledger.current_balance || 0);
          }

          // Opening balance
          if (ledger.opening_balance_dc === 'Dr') {
            openingDrTotal += Math.abs(ledger.opening_balance || 0);
          } else if (ledger.opening_balance_dc === 'Cr') {
            openingCrTotal += Math.abs(ledger.opening_balance || 0);
          }
        });
      }

      // Recursively add child group totals
      if (group.childGroups && group.childGroups.length > 0) {
        group.childGroups.forEach(childGroupId => {
          const childTotals = calculateGroupRecursive(childGroupId);

          // Add child group's current balance totals
          const childGroupTotal = groupTotals[childGroupId];
          drTotal += childGroupTotal.drTotal;
          crTotal += childGroupTotal.crTotal;

          // Add child group's opening balance totals
          const childOpeningTotal = groupOpeningTotals[childGroupId];
          openingDrTotal += childOpeningTotal.drTotal;
          openingCrTotal += childOpeningTotal.crTotal;
        });
      }

      // Calculate net totals
      const currentNetBalance = drTotal - crTotal;
      const openingNetBalance = openingDrTotal - openingCrTotal;

      // Store the totals
      groupTotals[groupId] = {
        total: Math.abs(currentNetBalance),
        drTotal: drTotal,
        crTotal: crTotal,
        dc: currentNetBalance >= 0 ? 'Dr' : 'Cr'
      };

      groupOpeningTotals[groupId] = {
        total: Math.abs(openingNetBalance),
        drTotal: openingDrTotal,
        crTotal: openingCrTotal,
        dc: openingNetBalance >= 0 ? 'Dr' : 'Cr'
      };

      return {
        drTotal,
        crTotal,
        openingDrTotal,
        openingCrTotal
      };
    };

    // Start calculation from root groups (groups with no parent)
    const rootGroups = Object.keys(hierarchy).filter(groupId =>
      !hierarchy[groupId].parent_id || hierarchy[groupId].is_main
    );

    rootGroups.forEach(groupId => {
      calculateGroupRecursive(groupId);
    });

    return { groupTotals, groupOpeningTotals };
  }, []);

  // Process the API response to create a flat list
  const processAccountsData = useCallback((data) => {
    const accountsList = [];
    let totalGroups = 0;
    let totalLedgers = 0;

    const processGroup = (group, level = 0) => {
      // Add the group to the list
      const groupEntry = {
        id: group.id,
        type: 'group',
        code: group.code,
        name: group.name,
        level: level,
        parent_id: group.g_parent_id,
        affects_gross: group.g_affects_gross,
        children_count: (group.children_groups?.length || 0) + (group.children_ledgers?.length || 0),
        is_main: group.g_parent_id === null,
        groupData: group,
        op_balance_dc: group.op_balance_dc,
        cg_balance_dc: group.cg_balance_dc
      };
      accountsList.push(groupEntry);
      totalGroups++;

      // Process child groups
      if (group.children_groups && group.children_groups.length > 0) {
        group.children_groups.forEach(childGroup => {
          processGroup(childGroup, level + 1);
        });
      }

      // Process child ledgers
      if (group.children_ledgers && group.children_ledgers.length > 0) {
        group.children_ledgers.forEach(ledger => {
          accountsList.push({
            id: ledger.id,
            type: 'ledger',
            code: ledger.code,
            name: ledger.name,
            level: level + 1,
            group_id: group.id,
            group_name: group.name,
            opening_balance: parseFloat(ledger.op_total || 0),
            opening_balance_dc: ledger.op_total_dc === 'D' ? 'Dr' : 'Cr',
            current_balance: parseFloat(ledger.cl_total || 0),
            current_balance_dc: ledger.cl_total_dc === 'D' ? 'Dr' : 'Cr',
            dr_total: parseFloat(ledger.dr_total || 0),
            cr_total: parseFloat(ledger.cr_total || 0),
            ledger_type: ledger.l_type,
            reconciliation: ledger.l_reconciliation,
            notes: ledger.l_notes,
            ledgerData: ledger
          });
          totalLedgers++;
        });
      }
    };

    // Process the chart_of_accounts array
    if (data.chart_of_accounts && Array.isArray(data.chart_of_accounts)) {
      data.chart_of_accounts.forEach(account => {
        processGroup(account, 0);
      });
    }

    // Calculate total balance (sum of all ledger balances)
    const totalBalance = accountsList
      .filter(acc => acc.type === 'ledger')
      .reduce((sum, ledger) => sum + Math.abs(ledger.current_balance || 0), 0);

    setStats({
      totalGroups,
      totalLedgers,
      totalBalance
    });

    return accountsList;
  }, []);

  // Calculate opening difference
  const calculateOpeningDifference = (openingDiff) => {
    if (!openingDiff) return { balance: 0, dc: '' };

    const balance = parseFloat(openingDiff.opdiff_balance || 0);
    const dc = openingDiff.opdiff_balance_dc === 'D' ? 'Dr' : 'Cr';

    return { balance, dc };
  };

  // Calculate group totals when accounts change - UPDATED
  const { groupTotals, groupOpeningTotals } = useMemo(() => {
    if (mainAccounts.length === 0) {
      return { groupTotals: {}, groupOpeningTotals: {} };
    }

    const hierarchy = buildGroupHierarchy(mainAccounts);
    return calculateGroupTotals(mainAccounts, hierarchy);
  }, [mainAccounts, buildGroupHierarchy, calculateGroupTotals]);

  // Fetch data from API with date filters
  const fetchAccounts = useCallback(async (withDates = false) => {
    try {
      setLoading(true);
      setError(null);

      let url = BACKEND_URL;

      // Add date parameters if provided
      if (withDates && (startDate || endDate)) {
        const params = new URLSearchParams();
        if (startDate) params.append('start_date', startDate);
        if (endDate) params.append('end_date', endDate);
        url = `${BACKEND_URL}?${params.toString()}`;
      }

      console.log('Fetching from:', url);

      const response = await axios.get(url, { headers });
      console.log('API Response:', response.data);

      if (response.data && response.data.success) {
        // Process the API response
        const accountsData = processAccountsData(response.data);
        setMainAccounts(accountsData);

        // Set opening difference
        const opDiff = calculateOpeningDifference(response.data.opening_difference);
        setOpeningDifference(opDiff);

        // Auto-expand all parent groups by default
        const groups = accountsData.filter(acc => acc.type === 'group' && acc.children_count > 0);
        const expanded = {};
        groups.forEach(group => {
          expanded[group.id] = true;
        });
        setExpandedGroups(expanded);
      } else {
        throw new Error(response.data?.message || 'Failed to fetch accounts');
      }
    } catch (err) {
      console.error('Error fetching accounts:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to load accounts';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, processAccountsData]);

  // Initial fetch without date filters
  useEffect(() => {
    fetchAccounts(false);
  }, []);

  // Handle date filter apply
  const handleApplyDateFilter = () => {
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      setError('Start date cannot be after end date');
      return;
    }
    fetchAccounts(true);
  };

  // Handle reset date filter
  const handleResetDateFilter = () => {
    setStartDate('');
    setEndDate('');
    setShowDateFilters(false);
    fetchAccounts(false);
  };

  // Format date for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Toggle group expansion
  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  // Expand all groups
  const expandAllGroups = () => {
    const groups = mainAccounts.filter(acc => acc.type === 'group');
    const expanded = {};
    groups.forEach(group => {
      expanded[group.id] = true;
    });
    setExpandedGroups(expanded);
  };

  // Collapse all groups
  const collapseAllGroups = () => {
    setExpandedGroups({});
  };

  // Check if a group should be shown (based on parent expansion)
  const shouldShowAccount = useCallback((account) => {
    if (account.level === 0) return true; // Always show top-level accounts

    if (account.type === 'group') {
      if (!account.parent_id) return true; // Root level group

      // Check all parent hierarchy
      let currentParentId = account.parent_id;
      while (currentParentId) {
        const parentGroup = mainAccounts.find(acc =>
          acc.type === 'group' && acc.id === currentParentId
        );
        if (!parentGroup) break;
        if (!expandedGroups[parentGroup.id]) return false;
        currentParentId = parentGroup.parent_id;
      }
      return true;
    } else if (account.type === 'ledger') {
      const parentGroup = mainAccounts.find(acc =>
        acc.type === 'group' && acc.id === account.group_id
      );
      if (!parentGroup) return true;
      return expandedGroups[parentGroup.id] || false;
    }
    return true;
  }, [mainAccounts, expandedGroups]);

  // Filter accounts based on search
  const getFilteredAccounts = useCallback(() => {
    if (!searchTerm.trim()) return mainAccounts;

    const term = searchTerm.toLowerCase();
    return mainAccounts.filter(acc =>
      acc.name.toLowerCase().includes(term) ||
      (acc.code && acc.code.toLowerCase().includes(term)) ||
      (acc.notes && acc.notes.toLowerCase().includes(term))
    );
  }, [mainAccounts, searchTerm]);

  // Format number (without currency symbol)
  const formatNumber = (amount) => {
    if (amount === undefined || amount === null) return '0.00';
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Math.abs(amount));
  };

  // Get balance display with Dr/Cr indicator
  const getBalanceDisplay = (amount, dc) => {
    if (amount === 0 || !dc) return '0.00';
    const formattedAmount = formatNumber(amount);
    return `${dc} ${formattedAmount}`;
  };

  // Get balance color class
  const getBalanceColorClass = (dc) => {
    if (dc === 'Dr') return 'balance-dr';
    if (dc === 'Cr') return 'balance-cr';
    return '';
  };

  // Get opening difference display
  const getOpeningDifferenceDisplay = () => {
    const { balance, dc } = openingDifference;
    if (balance === 0) return 'Balanced';
    return `${dc} ${formatNumber(balance)}`;
  };

  // Get the accounts to display
  const displayAccounts = useMemo(() => {
    const filtered = getFilteredAccounts();
    return filtered.filter(acc => shouldShowAccount(acc));
  }, [getFilteredAccounts, shouldShowAccount]);

  // DEBUG: Log group totals to console
  useEffect(() => {
    if (Object.keys(groupTotals).length > 0) {
      console.log('Group Totals:', groupTotals);
      console.log('Group Opening Totals:', groupOpeningTotals);
    }
  }, [groupTotals, groupOpeningTotals]);

  // Render loading state
  if (loading) {
    return (
      <div className="chart-container loading-container">
        <div className="spinner"></div>
        <div className="loading-text">Loading Chart of Accounts...</div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          <div className="row w-100">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 className="mb-sm-0 font-size-18">Chart of Accounts</h4>
              </div>
            </div>
          </div>
          <div className="chart-container">
            {/* Header */}
            <div className="chart-header">
              <div className="header-left">
                <h1>Chart of Accounts</h1>
                <p className="subtitle">Complete list of all account groups and ledgers</p>
              </div>
              <div className="header-right">
                <div className={`opening-diff ${openingDifference.balance === 0 ? 'balanced' : 'unbalanced'}`}>
                  <span className="diff-label">Opening Difference:</span>
                  <span className="diff-amount">{getOpeningDifferenceDisplay()}</span>
                </div>
                <button className="refresh-btn" onClick={() => fetchAccounts(true)} title="Refresh">
                  ↻
                </button>
              </div>
            </div>

            {/* Date Filter Controls */}
            <div className="date-filter-bar">
              <div className="date-filter-left">
                <button
                  className="date-filter-toggle"
                  onClick={() => setShowDateFilters(!showDateFilters)}
                >
                  {showDateFilters ? 'Hide Date Filters' : 'Show Date Filters'}
                </button>
              </div>

              {showDateFilters && (
                <div className="date-filter-right">
                  <div className="date-input-group">
                    <div className="date-input">
                      <label htmlFor="startDate">From Date</label>
                      {/* <input
                        type="date"
                        id="startDate"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="date-input-field"
                      /> */}
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          views={["year", "month", "day"]}
                          format="DD-MM-YYYY"
                          value={getDayjsDate(startDate)}
                          onChange={(newValue) => {
                            setStartDate(newValue ? newValue.format('YYYY-MM-DD') : '');
                          }}
                          slotProps={{
                            textField: { size: "small", fullWidth: true },
                            popper: { sx: { "& .MuiPaper-root": { fontSize: "0.7rem", minWidth: 260 } } },
                          }}
                        />
                      </LocalizationProvider>

                    </div>
                    <div className="date-input">
                      <label htmlFor="endDate">To Date</label>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>

                        <DatePicker
                          views={["year", "month", "day"]}
                          format="DD-MM-YYYY"
                          value={getDayjsDate(endDate)}
                          onChange={(newValue) => {
                            setEndDate(newValue ? newValue.format('YYYY-MM-DD') : '');
                          }}
                          slotProps={{
                            textField: { size: "small", fullWidth: true },
                            popper: { sx: { "& .MuiPaper-root": { fontSize: "0.7rem", minWidth: 260 } } },
                          }}
                        />
                      </LocalizationProvider>
                    </div>
                    <div className="date-buttons">
                      <button
                        className="apply-date-btn"
                        onClick={handleApplyDateFilter}
                        disabled={loading}
                      >
                        Apply Filter
                      </button>
                      <button
                        className="reset-date-btn"
                        onClick={handleResetDateFilter}
                      >
                        Reset
                      </button>
                    </div>
                  </div>

                  {(startDate || endDate) && (
                    <div className="date-display">
                      <span className="date-display-label">Filtering by: </span>
                      {startDate && <span className="date-display-value">From {formatDateForDisplay(startDate)}</span>}
                      {endDate && <span className="date-display-value">To {formatDateForDisplay(endDate)}</span>}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Controls Bar */}
            <div className="controls-bar">
              <div className="controls-left">
                <div className="expand-buttons">
                  <button className="expand-btn" onClick={expandAllGroups}>Expand All</button>
                  <button className="expand-btn" onClick={collapseAllGroups}>Collapse All</button>
                </div>
              </div>

              <div className="controls-right">
                <div className="search-box">
                  <input
                    type="text"
                    placeholder="Search accounts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  <span className="search-icon"></span>
                </div>

                <div className="stats">
                  <span className="stat-item">
                    <span className="stat-icon"></span>
                    {stats.totalGroups} Groups
                  </span>
                  <span className="stat-item">
                    <span className="stat-icon"></span>
                    {stats.totalLedgers} Ledgers
                  </span>
                </div>
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="error-alert">
                <div className="error-message">{error}</div>
                <button className="error-close" onClick={() => setError(null)}>×</button>
              </div>
            )}

            {/* Accounts Table */}
            <div className="accounts-table-container">
              <table className="table align-middle table-nowrap table-check1 table-list1">
                <thead className="table-sales-gradient">
                  <tr>
                    <th style={{ width: '50px' }}></th>
                    <th style={{ width: '35%', minWidth: '250px' }}>Account Name</th>
                    <th style={{ width: '80px' }}>Type</th>
                    <th style={{ width: '160px', textAlign: 'right' }}>OP Balance (AED)</th>
                    <th style={{ width: '160px', textAlign: 'right' }}>C/L Balance (AED)</th>
                  </tr>
                </thead>
                <tbody>
                  {displayAccounts.map((account) => {
                    const isGroup = account.type === 'group';
                    const isExpanded = expandedGroups[account.id];
                    const hasChildren = isGroup && account.children_count > 0;

                    // Get group totals if this is a group
                    const groupTotal = groupTotals[account.id];
                    const groupOpeningTotal = groupOpeningTotals[account.id];

                    // Debug logging for Asset group
                    if (isGroup && account.name === 'Assets') {
                      console.log('Asset Group Data:', {
                        id: account.id,
                        name: account.name,
                        groupTotal,
                        groupOpeningTotal,
                        children_count: account.children_count
                      });
                    }

                    return (
                      <tr
                        key={`${account.type}-${account.id}`}
                        className={`account-row ${isGroup ? 'group-row' : 'ledger-row'} ${isExpanded ? 'expanded' : ''}`}
                        style={{
                          backgroundColor: account.level % 2 === 0 ? '#f8f9fa' : '#ffffff',
                          borderLeft: account.is_main ? '4px solid #1976d2' : 'none'
                        }}
                      >
                        {/* First column - for expand/collapse buttons only */}
                        <td>
                          <div className="indentation-container">
                            {isGroup && hasChildren && (
                              <button
                                className="expand-toggle"
                                onClick={() => toggleGroup(account.id)}
                                title={isExpanded ? "Collapse" : "Expand"}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  width: '20px',
                                  padding: 0
                                }}
                              >
                                {isExpanded ? '▼' : '►'}
                              </button>
                            )}
                            {!isGroup && (
                              <div style={{ width: '20px', textAlign: 'center' }}>
                                <span className="ledger-arrow">→</span>
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Account Name with proper indentation */}
                        <td>
                          <div className="name-container">
                            <div
                              className="account-name-wrapper"
                              style={{
                                paddingLeft: `${account.level * 24}px`,
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              <span className={`${isGroup ? 'group-name' : 'ledger-name'}`}>
                                {account.name}
                                {isGroup && account.children_count > 0 && (
                                  <span className="children-count" style={{ marginLeft: '5px', color: '#6c757d', fontSize: '0.85em' }}>
                                    ({account.children_count})
                                  </span>
                                )}
                              </span>
                            </div>
                          </div>
                        </td>

                        {/* Type */}
                        <td>
                          <span className={`badge ${isGroup ? 'bg-light text-dark' : 'bg-light text-dark'} fw-normal`}>
                            {isGroup ? 'Group' : 'Ledger'}
                          </span>
                        </td>

                        {/* Opening Balance */}
                        <td style={{ textAlign: 'right' }}>
                          {isGroup ? (
                            <span
                              className="op-balance-group"
                              style={{
                                color: '#1a1a1a', // Dark black for groups
                                fontWeight: '600',
                                fontSize: 'inherit'
                              }}
                            >
                              {groupOpeningTotal ?
                                getBalanceDisplay(groupOpeningTotal.total, groupOpeningTotal.dc) :
                                '0.00'}
                            </span>
                          ) : (
                            <span
                              className="op-balance-ledger"
                              style={{
                                color: '#424242ff', // Light black/muted for ledgers
                                fontWeight: '400',
                                fontSize: 'inherit'
                              }}
                            >
                              {account.opening_balance === 0 ? '0.00' :
                                getBalanceDisplay(account.opening_balance, account.opening_balance_dc)}
                            </span>
                          )}
                        </td>

                        <td style={{ textAlign: 'right' }}>
                          {isGroup ? (
                            <span
                              className="cg-balance-group"
                              style={{
                                color: '#1a1a1a', // Dark black for groups
                                fontWeight: '600',
                                fontSize: 'inherit'
                              }}
                            >
                              {groupTotal ?
                                getBalanceDisplay(groupTotal.total, groupTotal.dc) :
                                '0.00'}
                            </span>
                          ) : (
                            <span
                              className="cg-balance-ledger"
                              style={{
                                color: '#424242ff', // Light black/muted for ledgers
                                fontWeight: '400',
                                fontSize: 'inherit'
                              }}
                            >
                              {account.current_balance === 0 ? '0.00' :
                                getBalanceDisplay(account.current_balance, account.current_balance_dc)}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Empty State */}
                  {displayAccounts.length === 0 && (
                    <tr className="empty-row">
                      <td colSpan="5" className="text-center py-4">
                        <div className="empty-state">
                          {searchTerm
                            ? `No accounts found matching "${searchTerm}"`
                            : 'No accounts available'
                          }
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="table-footer">
              <div className="footer-left">
                Showing {displayAccounts.length} of {mainAccounts.length} accounts
                {(startDate || endDate) && (
                  <span className="date-filter-info">
                    • Filtered by date range
                  </span>
                )}
              </div>
              <div className="footer-right">
                <span className="footer-stat">Total Groups: {stats.totalGroups}</span>
                <span className="footer-stat">Total Ledgers: {stats.totalLedgers}</span>
                <span className="footer-stat">Total Balance: {formatNumber(stats.totalBalance)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartOfAccounts;
